import React, { useState } from 'react';

import { token } from '@atlaskit/tokens';

import type { BaseViewPageProps } from '@confluence/view-page-common';
import {
	StartViewPageLoad,
	ViewPageCommon,
	useViewPagePersistentScrollPosition,
} from '@confluence/view-page-common';
import { useEventHandlers } from '@confluence/adf-renderer';

import { useTrackingConfig } from '../embedded-common/useTrackingConfig';
import { usePageTheme } from '../embedded-common/usePageTheme';

import { useViewPageFeatures } from './useViewPageFeatures';
import { HeaderContainer } from './HeaderContainer';

const CONTENT_SCREEN_STYLES: React.CSSProperties = {
	padding: `0px ${token('space.200')} 0px 0px`,
};

type EmbeddedViewPageProps = BaseViewPageProps & {
	classicComments?: React.ComponentType<any>;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	showHeaderButtons?: boolean;
};

export const EmbeddedViewPage: React.FC<EmbeddedViewPageProps> = ({
	classicComments,
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	showHeaderButtons = true, // for backwards compatibility
}) => {
	usePageTheme();
	const {
		hasByLineContributors,
		hasByLineExtensions,
		hasComments,
		hasInlineCommentsAndHighlightActions,
		hasPageReactions,
		hasStickyHeader,
		showDelete,
		showEdit,
		includeNonLicensedUsersToShare,
	} = useViewPageFeatures();
	const customTrackingConfig = useTrackingConfig();

	const eventHandlers = useEventHandlers();

	useViewPagePersistentScrollPosition(contentId, 'EP');

	const [hasCoverPicture, setHasCoverPicture] = useState(false);

	return (
		<>
			<StartViewPageLoad contentId={contentId} customConfig={customTrackingConfig} />
			<ViewPageCommon
				classicComments={classicComments}
				contentId={contentId}
				contentScreenStyles={CONTENT_SCREEN_STYLES}
				eventHandlers={eventHandlers}
				hasSatisfactionSurveys={false}
				hasEOPRecs={false}
				hasInlineComments={hasInlineCommentsAndHighlightActions}
				hasLabels={false}
				hasReactions={hasPageReactions}
				hasPageComments={hasComments}
				hasInlineActions={hasInlineCommentsAndHighlightActions}
				spaceKey={spaceKey}
				setHasCoverPicture={setHasCoverPicture}
				isEmbeddedPage
				titleAndByLineOptions={{
					hasByLineContributors,
					hasByLineExtensions,
					headingContainer: ({ children, showInlineCommentButton }) => (
						<HeaderContainer
							contentId={contentId}
							showInlineCommentButton={showHeaderButtons && showInlineCommentButton}
							hasInlineComments={hasInlineCommentsAndHighlightActions}
							hasStickyHeader={hasStickyHeader}
							spaceKey={spaceKey}
							showDelete={showHeaderButtons && showDelete}
							showEdit={showHeaderButtons && showEdit}
							// Requested by product to disable share button in embedded view page
							// Context: https://atlassian.slack.com/archives/C0131PNA4GL/p1731449321514049
							// TODO: clean up the code after we decided what to do for this feature.
							showShare={false}
							includeNonLicensedUsersToShare={includeNonLicensedUsersToShare}
							hasCoverPicture={hasCoverPicture}
						>
							{children}
						</HeaderContainer>
					),
				}}
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
			/>
		</>
	);
};
